import { useState, useEffect, useCallback } from "react";
import { Feedback, FeedbackTypeEnum, FeedbackStatusEnum } from "types";
import { handleAxiosAndCaughtError } from "utils";
import Loader from "components/Loader";
import GenericTable from "components/table";
import { Dialog } from "components/dialog";
import { useAuthContext } from "auth/AuthProvider";
import { getFeedbackApprovedByAgent } from "api/axios";
import { useAgentInfo } from "layouts/agent/agent-info/_context/AgentInfoContext";
import { useTranslation } from "react-i18next";

const FeedbacksTab = () => {
  const { t } = useTranslation();
  const { agentInfo } = useAgentInfo();
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingRowId, setUpdatingRowId] = useState<string | null>(null);
  const { hasPermission } = useAuthContext();
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);

  const fetchFeedbacks = useCallback(async (signal?: AbortSignal) => {
    if (!agentInfo?.id) return;
    
    setLoading(true);
    setError(null);
    try {
      const resp = await getFeedbackApprovedByAgent({
        agent_id: agentInfo.id,
        take: 999,
        skip: 0,
      });
      setFeedbacks(resp.items || []);
    } catch (err: any) {
      if (signal?.aborted || err.name === "CanceledError") {
        return;
      }
      setError("Click here to retry");
      setFeedbacks([]);
      handleAxiosAndCaughtError(err);
    } finally {
      setLoading(false);
    }
  }, [agentInfo?.id]);

  useEffect(() => {
    const abortController = new AbortController();
    fetchFeedbacks(abortController.signal);

    return () => abortController.abort();
  }, [fetchFeedbacks]);

  const handleViewFeedback = (feedback: Feedback) => {
    setSelectedFeedback(feedback);
    setIsViewDialogOpen(true);
  };

  const getResponseContent = (feedback: Feedback) => {
    if (feedback.feedback_type === FeedbackTypeEnum.CONTRIBUTE) {
      return feedback.ai_response;
    } else if (feedback.feedback_type === FeedbackTypeEnum.DISLIKE) {
      return feedback.feedback_message;
    }
    return "";
  };

  const columns = [
    {
      accessorKey: "refined_message",
      header: () => <p>{t("feedback.refinedMessage")}</p>,
      cell: (info: any) => {
        return (
          <div className="max-w-md">
            <p className="text-sm text-navy-700 dark:text-white whitespace-pre-wrap">
              {info.getValue() ?? " "}
            </p>
          </div>
        );
      },
    },
    {
      accessorKey: "response",
      header: () => <p>{t("feedback.response")}</p>,
      cell: (info: any) => {
        const feedback = info.row.original as Feedback;
        const responseContent = getResponseContent(feedback);
        return (
          <div className="max-w-md">
            <p className="text-sm text-navy-700 dark:text-white whitespace-pre-wrap">
              {responseContent}
            </p>
          </div>
        );
      },
    },
  ];

  const dataWithActions = feedbacks.map((row) => ({
    ...row,
    updatingRowId,
  }));

  const renderRowClassName = (row: any) => {
    const rowId = row.original.id;
    return updatingRowId === rowId ? "animate-pulse opacity-60" : "";
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader txt={t("feedback.loadingFeedback")} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div
          className="cursor-pointer text-red-500 hover:text-red-700"
          onClick={() => fetchFeedbacks()}
        >
          {error}
        </div>
      </div>
    );
  }

  return (
    <>
      <GenericTable
        data={dataWithActions}
        columns={columns as any}
        searchPlaceholder={t("feedback.searchFeedbacks")}
        debug={true}
        renderRowClassName={renderRowClassName}
        onRowClick={(item) => {
          handleViewFeedback(item);
        }}
      />
      
      {/* View Feedback Dialog */}
      <Dialog isOpen={isViewDialogOpen} setIsOpen={setIsViewDialogOpen} outsideClickDisabled={true} backdropHandlerDisabled={true}>
        <div className="flex min-h-[10vh] w-full max-w-[700px] flex-col gap-4 sm:gap-6">
          <div className="border-b border-gray-200 pb-3 sm:pb-4">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white sm:text-xl">
              {t("feedback.feedbackDetails")}
            </h2>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
              {t("feedback.viewFeedbackDetails")}
            </p>
          </div>

          {selectedFeedback ? (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t("feedback.refinedMessage")}
                </label>
                <div className="mt-1 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-600 dark:bg-gray-800">
                  <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">
                    {selectedFeedback.refined_message ?? " "}
                  </p>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t("feedback.response")}
                </label>
                <div className="mt-1 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-600 dark:bg-gray-800">
                  <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">
                    {getResponseContent(selectedFeedback)}
                  </p>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </Dialog>
    </>
  );
};

export default FeedbacksTab; 